import {
  ErrorApi,
  ErrorNotLoggedIn,
  Paragraph,
  Preloader,
  Spinner,
} from 'src/components'
import { savedTokens } from 'src/auth'
import { useGetOrganization } from '../Info/queries'
import { TrackedTextField, TrackedSaveButton } from '../Info/TrackedInputs'
import {
  savedSfLoginUrl,
  savedSfPrincipalArn,
  savedSfRoleArn,
} from '../Info/storage'
import { toast } from 'sonner'
type Props = {
  organization: string
  username: string
}

function SSO(props: Props) {
  const { status, data, error, refetch } = useGetOrganization({
    organization: props.organization,
    username: props.username,
  })

  if (status === 'loading' || status === 'idle') {
    return <Preloader />
  }

  if (status === 'error') {
    return <ErrorApi message={error} onClick={refetch} />
  }

  if (data.setupStep !== 'finished') {
    toast.warning(`You didn't finish the setup yet.`)
    return null // FIXME: how to handle this case?
  }

  const SSOComponent = () => {
    if (data.sso) {
      switch (data.sso?.type) {
        case 'awsiam':
          return <AWSIAMSSO sso={data.sso} />
        case 'googleworkspace':
          return <GoogleWorkspaceSSO sso={data.sso} />
        case 'azure':
        default:
          return <AzureSSO sso={data.sso} />
      }
    } else {
      return <Paragraph>Something went wrong.</Paragraph>
    }
  }

  return (
    <>
      {data.sso ? SSOComponent() : <Spinner />}

      <TrackedSaveButton
        organization={props.organization}
        username={props.username}
      />
    </>
  )
}

export default function ValidateInfo() {
  const [tokens] = savedTokens.useState(null)

  if (!tokens) {
    return <ErrorNotLoggedIn />
  }

  return <SSO organization={tokens.organization} username={tokens.username} />
}

type PropsSSO = {
  sso: {
    loginUrl: string
    principalArn: string
    roleArn: string
    region: string
    type?: string
  }
}

const AzureSSO = (props: PropsSSO) => {
  return (
    <div className="grid grid-cols-2 gap-6 px-2 mt-3">
      <TrackedTextField
        trimValue
        savedStorage={savedSfLoginUrl}
        label="SSO Login Url"
        placeholder="https://myapps.microsoft.com/signin"
        title="sso.loginUrl"
        initialValue={props.sso.loginUrl}
        type="url"
        tooltip={
          <p>
            Navigate to{' '}
            <b>
              Azure portal -&gt; Properties -&gt; Amazon Web Services
              application -&gt; properties -&gt; User Access URL
            </b>
          </p>
        }
      />

      <TrackedTextField
        trimValue
        savedStorage={savedSfPrincipalArn}
        label="Identity provider ARN"
        pattern="arn:aws:iam::\d{12}:saml-provider\/[A-Za-z0-9\-_]+"
        title="sso.principalArn"
        initialValue={props.sso.principalArn}
        placeholder="arn:aws:iam::ACCOUNT_ID:saml-provider/PROVIDER_ID"
        tooltip={
          <p>
            Navigate to{' '}
            <b>
              AWS Management console -&gt; IAM -&gt; Identity Providers -&gt;
              AzureAD_Connect_Admin -&gt; ARN
            </b>
          </p>
        }
      />

      <TrackedTextField
        trimValue
        savedStorage={savedSfRoleArn}
        label="Role ARN"
        title="sso.roleArn"
        initialValue={props.sso.roleArn}
        placeholder="arn:aws:iam::ACCOUNT_ID:role/ROLE_ID"
        pattern="arn:aws:iam::\d{12}:role\/[A-Za-z0-9\-_]+"
        tooltip={
          <p>
            Navigate to{' '}
            <b>
              AWS Management console -&gt; IAM -&gt; Roles -&gt; Click Azure
              role -&gt; ARN
            </b>
          </p>
        }
      />
    </div>
  )
}

const AWSIAMSSO = (props: PropsSSO) => {
  return (
    <div className="grid grid-cols-2 gap-6 px-2 mt-3">
      <TrackedTextField
        trimValue
        savedStorage={savedSfLoginUrl}
        label="SSO Login Url"
        placeholder="https://#ID.awsapps.com/start/#/saml/default/Mobile%20Connect/ins-#ID"
        title="sso.loginUrl"
        initialValue={props.sso.loginUrl}
        type="url"
        tooltip={
          <img
            src={require('src/images/awsiam_login.png')}
            width="600"
            alt="AWS IAM where to get login url - "
          />
        }
      />

      <TrackedTextField
        trimValue
        savedStorage={savedSfPrincipalArn}
        label="Identity provider ARN"
        pattern="arn:aws:iam::\d{12}:saml-provider\/[A-Za-z0-9\-_]+"
        title="sso.principalArn"
        initialValue={props.sso.principalArn}
        placeholder="arn:aws:iam::ACCOUNT_ID:saml-provider/PROVIDER_ID"
        tooltip={
          <p>
            Navigate to{' '}
            <b>
              AWS Management console -&gt; IAM -&gt; Identity Providers -&gt;
              IdentityCenter-SSO -&gt; ARN
            </b>
          </p>
        }
      />

      <TrackedTextField
        trimValue
        savedStorage={savedSfRoleArn}
        label="Role ARN"
        title="sso.roleArn"
        initialValue={props.sso.roleArn}
        placeholder="arn:aws:iam::ACCOUNT_ID:role/ROLE_ID"
        pattern="arn:aws:iam::\d{12}:role\/[A-Za-z0-9\-_]+"
        tooltip={
          <p>
            Navigate to{' '}
            <b>
              AWS Management console -&gt; IAM -&gt; Roles -&gt; Click
              “IdentityCenter_SAML_Role” role -&gt; ARN
            </b>
          </p>
        }
      />
    </div>
  )
}

const GoogleWorkspaceSSO = (props: PropsSSO) => {
  return (
    <div className="grid grid-cols-2 gap-6 px-2 mt-3">
      <TrackedTextField
        trimValue
        savedStorage={savedSfLoginUrl}
        label="SSO Login Url"
        placeholder="https://accounts.google.com/o/saml2/initsso?idpid=#IDPID&spid=#SPID"
        title="sso.loginUrl"
        initialValue={props.sso.loginUrl}
        type="url"
        tooltip={
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Paragraph className="font-bold">idpid</Paragraph>
              <img
                width={400}
                alt="idpid"
                src={require('src/images/idpid.png')}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Paragraph className="font-bold">spid</Paragraph>
              <img
                width={600}
                alt="spid"
                src={require('src/images/spid.png')}
              />
            </div>
          </div>
        }
      />

      <TrackedTextField
        trimValue
        savedStorage={savedSfPrincipalArn}
        label="Identity provider ARN"
        pattern="arn:aws:iam::\d{12}:saml-provider\/[A-Za-z0-9\-_]+"
        title="sso.principalArn"
        initialValue={props.sso.principalArn}
        placeholder="arn:aws:iam::ACCOUNT_ID:saml-provider/PROVIDER_ID"
        tooltip={
          <p>
            Navigate to{' '}
            <b>
              AWS Management console -&gt; IAM -&gt; Identity Providers -&gt;
              IdentityCenter-SSO -&gt; ARN
            </b>
          </p>
        }
      />

      <TrackedTextField
        trimValue
        savedStorage={savedSfRoleArn}
        label="Role ARN"
        title="sso.roleArn"
        initialValue={props.sso.roleArn}
        placeholder="arn:aws:iam::ACCOUNT_ID:role/ROLE_ID"
        pattern="arn:aws:iam::\d{12}:role\/[A-Za-z0-9\-_]+"
        tooltip={
          <p>
            Navigate to{' '}
            <b>
              AWS Management console -&gt; IAM -&gt; Roles -&gt; Click
              “IdentityCenter_SAML_Role” role -&gt; ARN
            </b>
          </p>
        }
      />
    </div>
  )
}
